// src/pages/BotCreationWizard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './BotCreationWizard.css';
import swal from 'sweetalert2';

const BotCreationWizard = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  
  // Estado para almacenar la plantilla seleccionada
  const [template, setTemplate] = useState(null);
  
  // Estado para los datos del bot
  const [botData, setBotData] = useState({
    name: '',
    description: '',
    bot_type: '',
    input_providers: [],
    action_providers: [],
    output_providers: [],
    connections: [],
    system_prompt: ''
  });

  // Datos de ejemplo para proveedores disponibles
  const availableProviders = [
    { id: 1, name: "Gmail", type: "email", service: "input" },
    { id: 2, name: "WhatsApp", type: "social", service: "input" },
    { id: 3, name: "Telegram", type: "social", service: "input" },
    { id: 4, name: "SMS Twilio", type: "app", service: "input" },
    { id: 5, name: "Chat Web", type: "app", service: "input" },
    { id: 6, name: "CRM Salesforce", type: "app", service: "action" },
    { id: 7, name: "Google Calendar", type: "app", service: "action" },
    { id: 8, name: "Trello", type: "app", service: "action" },
    { id: 9, name: "Sheets", type: "app", service: "action" },
    { id: 10, name: "Gmail", type: "email", service: "output" },
    { id: 11, name: "WhatsApp", type: "social", service: "output" },
    { id: 12, name: "Telegram", type: "social", service: "output" },
    { id: 13, name: "SMS Twilio", type: "app", service: "output" }
  ];

  // Datos de ejemplo para plantillas de bot
  const botTemplates = [
    {
      id: 1,
      name: "Asistente Comercial",
      bot_type: "commercial",
      description: "Perfecto para atención al cliente y ventas. Responde consultas, proporciona información de productos y deriva a agentes cuando es necesario.",
      input_provider_ids: [1, 2], // Email y WhatsApp
      action_provider_ids: [6], // CRM
      output_provider_ids: [10, 11] // Email y WhatsApp
    },
    {
      id: 2,
      name: "Agente Asistente Personal",
      bot_type: "personal",
      description: "Ayuda con la gestión de calendario, recordatorios y tareas personales.",
      input_provider_ids: [1, 3], // Email y Telegram
      action_provider_ids: [7, 8], // Calendar y Trello
      output_provider_ids: [10, 12] // Email y Telegram
    },
    {
      id: 3,
      name: "Bot de Soporte Web",
      bot_type: "website",
      description: "Diseñado para integrarse en tu sitio web y proporcionar soporte inmediato a los visitantes.",
      input_provider_ids: [5], // Chat Web
      action_provider_ids: [6, 9], // CRM y Sheets
      output_provider_ids: [5] // Chat Web
    }
  ];

  // Cargar datos de la plantilla al iniciar
  useEffect(() => {
    const loadTemplate = async () => {
      setIsLoading(true);
      
      try {
        // En una aplicación real, aquí haríamos una llamada a la API
        // Para este ejemplo, usamos los datos de muestra
        if (templateId) {
          const selectedTemplate = botTemplates.find(t => t.id === parseInt(templateId));
          
          if (selectedTemplate) {
            setTemplate(selectedTemplate);
            
            // Pre-configurar el bot con los datos de la plantilla
            setBotData(prev => ({
              ...prev,
              bot_type: selectedTemplate.bot_type,
              name: `Mi ${selectedTemplate.name}`,
              description: selectedTemplate.description,
              // Convertimos IDs a objetos completos de proveedores
              input_providers: selectedTemplate.input_provider_ids.map(id => 
                availableProviders.find(p => p.id === id)
              ).filter(Boolean),
              action_providers: selectedTemplate.action_provider_ids.map(id => 
                availableProviders.find(p => p.id === id)
              ).filter(Boolean),
              output_providers: selectedTemplate.output_provider_ids.map(id => 
                availableProviders.find(p => p.id === id)
              ).filter(Boolean)
            }));
          }
        }
      } catch (error) {
        console.error("Error al cargar la plantilla:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadTemplate();
  }, [templateId]);

  // Funciones para manejar selección de proveedores
  const handleSelectProvider = (provider, category) => {
    setBotData(prev => {
      // Determinar qué lista de proveedores actualizar
      const key = `${category}_providers`;
      
      // Verificar si el proveedor ya está seleccionado
      const isSelected = prev[key].some(p => p.id === provider.id);
      
      // Actualizar la lista de proveedores correspondiente
      return {
        ...prev,
        [key]: isSelected 
          ? prev[key].filter(p => p.id !== provider.id) // Quitar si ya estaba seleccionado
          : [...prev[key], provider] // Agregar si no estaba seleccionado
      };
    });
  };

  // Función para agregar una conexión
  const addConnection = (provider) => {
    setBotData(prev => {
      // Crear una nueva conexión
      const newConnection = {
        id: Date.now(), // ID temporal
        name: `Conexión ${provider.name}`,
        provider_id: provider.id,
        config: {}
      };
      
      return {
        ...prev,
        connections: [...prev.connections, newConnection]
      };
    });
  };

  // Función para actualizar una conexión
  const updateConnection = (connectionId, data) => {
    setBotData(prev => ({
      ...prev,
      connections: prev.connections.map(conn => 
        conn.id === connectionId ? { ...conn, ...data } : conn
      )
    }));
  };

  const nextStep = () => {
    window.scrollTo(0, 0);
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    window.scrollTo(0, 0);
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    
    try {
      // Create new bot object
      const newBot = {
        id: Date.now(),
        name: botData.name,
        description: botData.description,
        status: 'active',
        connections: botData.connections.length,
        lastActive: 'Hace 1 minuto',
        ...botData
      };
      
      // Get existing bots from localStorage
      const existingBots = JSON.parse(localStorage.getItem('bots') || '[]');
      
      // Add new bot to the list
      const updatedBots = [...existingBots, newBot];
      
      // Save to localStorage
      localStorage.setItem('bots', JSON.stringify(updatedBots));
      
      // Show success message
      swal.fire({
        title: '¡Bot creado!',
        text: 'Tu bot ha sido creado exitosamente.',
        icon: 'success',
        confirmButtonText: 'Continuar'
      }).then(() => {
        // Navigate to dashboard
        navigate('/dashboard');
      });
    } catch (error) {
      console.error("Error al crear el bot:", error);
      swal.fire({
        title: 'Error',
        text: 'Hubo un error al crear el bot. Por favor, intenta nuevamente.',
        icon: 'error',
        confirmButtonText: 'Entendido'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Renderizar el paso actual
  const renderStep = () => {
    switch (currentStep) {
      case 1: // Información básica
        return (
          <div className="wizard-step">
            <h2>Información Básica</h2>
            <p className="step-description">
              Proporciona la información básica para tu nuevo bot.
            </p>
            
            <div className="form-group">
              <label htmlFor="name">Nombre del Bot</label>
              <input
                type="text"
                id="name"
                value={botData.name}
                onChange={(e) => setBotData({...botData, name: e.target.value})}
                placeholder="Ej: Asistente de Ventas"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="bot_type">Tipo de Bot</label>
              <select
                id="bot_type"
                value={botData.bot_type}
                onChange={(e) => setBotData({...botData, bot_type: e.target.value})}
              >
                <option value="">Selecciona un tipo</option>
                <option value="commercial">Asistente Comercial</option>
                <option value="personal">Asistente Personal</option>
                <option value="website">Asistente Web</option>
                <option value="custom">Personalizado</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Descripción</label>
              <textarea
                id="description"
                value={botData.description}
                onChange={(e) => setBotData({...botData, description: e.target.value})}
                placeholder="Describe la función de este bot..."
                rows="4"
              ></textarea>
            </div>
            
            <div className="form-group">
              <label htmlFor="system_prompt">Instrucciones del Sistema</label>
              <textarea
                id="system_prompt"
                value={botData.system_prompt}
                onChange={(e) => setBotData({...botData, system_prompt: e.target.value})}
                placeholder="Instrucciones detalladas para el comportamiento del bot..."
                rows="6"
              ></textarea>
              <small className="form-text">Estas instrucciones definirán cómo debe comportarse el bot al interactuar con los usuarios.</small>
            </div>
          </div>
        );
      
      case 2: // Proveedores de entrada
        return (
          <div className="wizard-step">
            <h2>Proveedores de Entrada</h2>
            <p className="step-description">
              Selecciona los canales por los que tu bot recibirá mensajes o información.
            </p>
            
            <div className="providers-grid">
              {availableProviders
                .filter(provider => provider.service === 'input')
                .map(provider => {
                  const isSelected = botData.input_providers.some(p => p.id === provider.id);
                  
                  return (
                    <div 
                      key={provider.id}
                      className={`provider-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectProvider(provider, 'input')}
                    >
                      <div className={`provider-icon ${provider.type}`}>
                        <ProviderIcon type={provider.type} />
                      </div>
                      <h3>{provider.name}</h3>
                      <p>{getProviderTypeLabel(provider.type)}</p>
                      
                      {isSelected && (
                        <div className="selected-mark">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            
            {botData.input_providers.length > 0 && (
              <div className="selected-providers">
                <h3>Proveedores Seleccionados</h3>
                <div className="selected-providers-list">
                  {botData.input_providers.map(provider => (
                    <div key={provider.id} className="selected-provider-item">
                      <span>{provider.name}</span>
                      <button
                        className="connection-btn"
                        onClick={() => addConnection(provider)}
                      >
                        Configurar Conexión
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      case 3: // Proveedores de acción
        return (
          <div className="wizard-step">
            <h2>Proveedores de Acción</h2>
            <p className="step-description">
              Selecciona los servicios que tu bot utilizará para realizar acciones.
            </p>
            
            <div className="providers-grid">
              {availableProviders
                .filter(provider => provider.service === 'action')
                .map(provider => {
                  const isSelected = botData.action_providers.some(p => p.id === provider.id);
                  
                  return (
                    <div 
                      key={provider.id}
                      className={`provider-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectProvider(provider, 'action')}
                    >
                      <div className={`provider-icon ${provider.type}`}>
                        <ProviderIcon type={provider.type} />
                      </div>
                      <h3>{provider.name}</h3>
                      <p>{getProviderTypeLabel(provider.type)}</p>
                      
                      {isSelected && (
                        <div className="selected-mark">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            
            {botData.action_providers.length > 0 && (
              <div className="selected-providers">
                <h3>Proveedores Seleccionados</h3>
                <div className="selected-providers-list">
                  {botData.action_providers.map(provider => (
                    <div key={provider.id} className="selected-provider-item">
                      <span>{provider.name}</span>
                      <button
                        className="connection-btn"
                        onClick={() => addConnection(provider)}
                      >
                        Configurar Conexión
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      case 4: // Proveedores de salida
        return (
          <div className="wizard-step">
            <h2>Proveedores de Salida</h2>
            <p className="step-description">
              Selecciona los canales por los que tu bot enviará respuestas o información.
            </p>
            
            <div className="providers-grid">
              {availableProviders
                .filter(provider => provider.service === 'output')
                .map(provider => {
                  const isSelected = botData.output_providers.some(p => p.id === provider.id);
                  
                  return (
                    <div 
                      key={provider.id}
                      className={`provider-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectProvider(provider, 'output')}
                    >
                      <div className={`provider-icon ${provider.type}`}>
                        <ProviderIcon type={provider.type} />
                      </div>
                      <h3>{provider.name}</h3>
                      <p>{getProviderTypeLabel(provider.type)}</p>
                      
                      {isSelected && (
                        <div className="selected-mark">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            
            {botData.output_providers.length > 0 && (
              <div className="selected-providers">
                <h3>Proveedores Seleccionados</h3>
                <div className="selected-providers-list">
                  {botData.output_providers.map(provider => (
                    <div key={provider.id} className="selected-provider-item">
                      <span>{provider.name}</span>
                      <button
                        className="connection-btn"
                        onClick={() => addConnection(provider)}
                      >
                        Configurar Conexión
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      case 5: // Configuración de conexiones
        return (
          <div className="wizard-step">
            <h2>Configuración de Conexiones</h2>
            <p className="step-description">
              Configura las conexiones para cada proveedor seleccionado.
            </p>
            
            {botData.connections.length === 0 ? (
              <div className="no-connections">
                <p>No has creado ninguna conexión aún. Vuelve a los pasos anteriores y selecciona "Configurar Conexión" en los proveedores que necesites.</p>
              </div>
            ) : (
              <div className="connections-list">
                {botData.connections.map(connection => {
                  const provider = availableProviders.find(p => p.id === connection.provider_id);
                  
                  return (
                    <div key={connection.id} className="connection-card">
                      <div className="connection-header">
                        <h3>{connection.name}</h3>
                        <span className={`provider-type ${provider?.type || 'unknown'}`}>
                          {provider?.name || 'Desconocido'}
                        </span>
                      </div>
                      
                      <div className="connection-form">
                        <div className="form-group">
                          <label>Nombre de la Conexión</label>
                          <input
                            type="text"
                            value={connection.name}
                            onChange={(e) => updateConnection(connection.id, { name: e.target.value })}
                          />
                        </div>
                        
                        {provider?.type === 'email' && (
                          <>
                            <div className="form-group">
                              <label>Servidor SMTP</label>
                              <input
                                type="text"
                                value={connection.config?.smtp_server || ''}
                                onChange={(e) => updateConnection(connection.id, { 
                                  config: { ...connection.config, smtp_server: e.target.value } 
                                })}
                                placeholder="smtp.gmail.com"
                              />
                            </div>
                            <div className="form-row">
                              <div className="form-group">
                                <label>Puerto SMTP</label>
                                <input
                                  type="number"
                                  value={connection.config?.smtp_port || 587}
                                  onChange={(e) => updateConnection(connection.id, { 
                                    config: { ...connection.config, smtp_port: e.target.value } 
                                  })}
                                />
                              </div>
                              <div className="form-group">
                                <label>Cifrado</label>
                                <select
                                  value={connection.config?.smtp_encryption || 'tls'}
                                  onChange={(e) => updateConnection(connection.id, { 
                                    config: { ...connection.config, smtp_encryption: e.target.value } 
                                  })}
                                >
                                  <option value="tls">TLS</option>
                                  <option value="ssl">SSL</option>
                                  <option value="none">Ninguno</option>
                                </select>
                              </div>
                            </div>
                            <div className="form-group">
                              <label>Usuario SMTP</label>
                              <input
                                type="text"
                                value={connection.config?.smtp_user || ''}
                                onChange={(e) => updateConnection(connection.id, { 
                                  config: { ...connection.config, smtp_user: e.target.value } 
                                })}
                                placeholder="tu@email.com"
                              />
                            </div>
                            <div className="form-group">
                              <label>Contraseña SMTP</label>
                              <input
                                type="password"
                                value={connection.config?.smtp_password || ''}
                                onChange={(e) => updateConnection(connection.id, { 
                                  config: { ...connection.config, smtp_password: e.target.value } 
                                })}
                                placeholder="••••••••"
                              />
                            </div>
                          </>
                        )}
                        
                        {provider?.type === 'social' && (
                          <>
                            <div className="form-group">
                              <label>API Key</label>
                              <input
                                type="text"
                                value={connection.config?.api_key || ''}
                                onChange={(e) => updateConnection(connection.id, { 
                                  config: { ...connection.config, api_key: e.target.value } 
                                })}
                              />
                            </div>
                            <div className="form-group">
                              <label>API Secret</label>
                              <input
                                type="password"
                                value={connection.config?.api_secret || ''}
                                onChange={(e) => updateConnection(connection.id, { 
                                  config: { ...connection.config, api_secret: e.target.value } 
                                })}
                              />
                            </div>
                            <div className="form-group">
                              <label>Access Token</label>
                              <input
                                type="text"
                                value={connection.config?.access_token || ''}
                                onChange={(e) => updateConnection(connection.id, { 
                                  config: { ...connection.config, access_token: e.target.value } 
                                })}
                              />
                            </div>
                            <div className="form-group">
                              <label>URL del Webhook</label>
                              <input
                                type="text"
                                value={connection.config?.webhook_url || ''}
                                onChange={(e) => updateConnection(connection.id, { 
                                  config: { ...connection.config, webhook_url: e.target.value } 
                                })}
                                placeholder="https://..."
                              />
                            </div>
                          </>
                        )}
                        
                        {provider?.type === 'app' && (
                          <>
                            <div className="form-group">
                              <label>ID de Aplicación</label>
                              <input
                                type="text"
                                value={connection.config?.app_id || ''}
                                onChange={(e) => updateConnection(connection.id, { 
                                  config: { ...connection.config, app_id: e.target.value } 
                                })}
                              />
                            </div>
                            <div className="form-group">
                              <label>API Key</label>
                              <input
                                type="text"
                                value={connection.config?.api_key || ''}
                                onChange={(e) => updateConnection(connection.id, { 
                                  config: { ...connection.config, api_key: e.target.value } 
                                })}
                              />
                            </div>
                            <div className="form-group">
                              <label>URL del Endpoint</label>
                              <input
                                type="text"
                                value={connection.config?.endpoint_url || ''}
                                onChange={(e) => updateConnection(connection.id, { 
                                  config: { ...connection.config, endpoint_url: e.target.value } 
                                })}
                                placeholder="https://..."
                              />
                            </div>
                          </>
                        )}
                        
                        <div className="test-connection">
                          <button type="button" className="test-connection-btn">
                            Probar Conexión
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      
      case 6: // Resumen y confirmación
        return (
          <div className="wizard-step">
            <h2>Resumen del Bot</h2>
            <p className="step-description">
              Revisa la configuración de tu bot antes de crearlo.
            </p>
            
            <div className="summary-card">
              <div className="summary-section">
                <h3>Información Básica</h3>
                <div className="summary-item">
                  <span className="summary-label">Nombre:</span>
                  <span className="summary-value">{botData.name}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Tipo:</span>
                  <span className="summary-value">{getBotTypeLabel(botData.bot_type)}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Descripción:</span>
                  <span className="summary-value">{botData.description}</span>
                </div>
              </div>
              
              <div className="summary-section">
                <h3>Proveedores</h3>
                <div className="summary-item">
                  <span className="summary-label">Entrada:</span>
                  <span className="summary-value">
                    {botData.input_providers.length > 0 
                      ? botData.input_providers.map(p => p.name).join(', ')
                      : 'Ninguno seleccionado'}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Acción:</span>
                  <span className="summary-value">
                    {botData.action_providers.length > 0 
                      ? botData.action_providers.map(p => p.name).join(', ')
                      : 'Ninguno seleccionado'}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Salida:</span>
                  <span className="summary-value">
                    {botData.output_providers.length > 0 
                      ? botData.output_providers.map(p => p.name).join(', ')
                      : 'Ninguno seleccionado'}
                  </span>
                </div>
              </div>
              
              <div className="summary-section">
                <h3>Conexiones</h3>
                {botData.connections.length > 0 ? (
                  <ul className="connections-summary">
                    {botData.connections.map(connection => {
                      const provider = availableProviders.find(p => p.id === connection.provider_id);
                      return (
                        <li key={connection.id}>
                          {connection.name} ({provider?.name || 'Desconocido'})
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p>No se han configurado conexiones.</p>
                )}
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  // Función auxiliar para obtener etiqueta de tipo de proveedor
  const getProviderTypeLabel = (type) => {
    const labels = {
      'email': 'Correo Electrónico',
      'social': 'Red Social',
      'app': 'Aplicación'
    };
    return labels[type] || type;
  };

  // Función auxiliar para obtener etiqueta de tipo de bot
  const getBotTypeLabel = (type) => {
    const labels = {
      'commercial': 'Asistente Comercial',
      'personal': 'Asistente Personal',
      'website': 'Asistente Web',
      'custom': 'Personalizado'
    };
    return labels[type] || type;
  };

  // Componente para íconos de proveedores
  const ProviderIcon = ({ type }) => {
    switch(type) {
      case 'email':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        );
      case 'social':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        );
      case 'app':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="16" />
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
        );
    }
  };

 const ProgressIndicator = () => {
    const totalSteps = 6;
    
    return (
      <div className="progress-indicator">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div 
            key={i}
            className={`progress-step ${i + 1 === currentStep ? 'active' : i + 1 < currentStep ? 'completed' : ''}`}
            onClick={() => i + 1 < currentStep && setCurrentStep(i + 1)}
          >
            {i + 1 < currentStep ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              i + 1
            )}
          </div>
        ))}
      </div>
    );
  };

  if (isLoading && !template) {
    return (
      <div className="wizard-loading">
        <div className="loading-spinner"></div>
        <p>Cargando información de la plantilla...</p>
      </div>
    );
  }

  return (
    <div className="wizard-container">
      <div className="wizard-header">
        <h1>
          {templateId ? `Crear Bot a partir de "${template?.name || 'Plantilla'}"` : 'Crear Nuevo Bot'}
        </h1>
      </div>
      
      <ProgressIndicator />
      
      <div className="wizard-content">
        {renderStep()}
        
        <div className="wizard-actions">
          {currentStep > 1 && (
            <button 
              className="btn-secondary"
              onClick={prevStep}
              disabled={isLoading}
            >
              Anterior
            </button>
          )}
          
          {currentStep < 6 ? (
            <button 
              className="btn-primary"
              onClick={nextStep}
              disabled={isLoading}
            >
              Siguiente
            </button>
          ) : (
            <button 
              className="btn-success"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Creando Bot...' : 'Crear Bot'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BotCreationWizard;